(() => {
  'use strict';

  class ZBV4ProductController {
    constructor(root) {
      this.root = root;
      this.variants = {};

      const dataElement = root.querySelector('script[data-zbv4-variant-data]');
      if (dataElement) {
        try {
          this.variants = JSON.parse(dataElement.textContent || '{}');
        } catch (error) {
          console.error('Unable to read v4 product variant data.', error);
        }
      }

      root.querySelector('[data-zbv4-variant-select]')?.addEventListener('change', (event) => {
        this.selectVariant(event.currentTarget.value);
      });

      root.querySelectorAll('[data-zbv4-variant-choice]').forEach((choice) => {
        choice.addEventListener('click', () => {
          this.selectVariant(choice.dataset.zbv4VariantChoice);
        });
      });

      root.querySelectorAll('[data-zbv4-purchase-mode]').forEach((control) => {
        control.addEventListener('change', () => this.syncPurchaseMode(control));
      });

      root.querySelectorAll('[data-zbv4-media-thumbnail]').forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.activateMedia(thumbnail.dataset.zbv4MediaThumbnail));
      });

      root.querySelectorAll('[data-zbv4-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = root.querySelector('[data-zbv4-product-form] input[name="quantity"]');
          if (!input) return;
          const delta = Number(button.dataset.zbv4QuantityChange);
          input.value = String(Math.max(1, Number(input.value || 1) + delta));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncStickyQuantity(input.value);
        });
      });

      root.querySelector('[data-zbv4-product-form] input[name="quantity"]')?.addEventListener('change', (event) => {
        const quantity = Math.max(1, Number(event.currentTarget.value || 1));
        event.currentTarget.value = String(quantity);
        this.syncStickyQuantity(quantity);
      });

      const initialVariantId = root.dataset.selectedVariantId;
      if (initialVariantId && this.variants[String(initialVariantId)]) {
        this.selectVariant(initialVariantId, { updateUrl: false });
      }
    }

    selectVariant(variantId, { updateUrl = true } = {}) {
      const variant = this.variants[String(variantId)];
      if (!variant) return;

      this.root.dataset.selectedVariantId = String(variant.id);
      const selector = this.root.querySelector('[data-zbv4-variant-select]');
      if (selector && Array.from(selector.options).some((option) => option.value === String(variant.id))) {
        selector.value = String(variant.id);
      }
      this.root.querySelectorAll('[data-zbv4-variant-choice]').forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice.dataset.zbv4VariantChoice === String(variant.id)));
      });

      this.updateContent(variant);
      this.updateProductForms(variant);
      this.updateGallery(variant);
      this.updateSticky(variant);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('zbv4:variant-change', {
        bubbles: true,
        detail: variant
      }));
    }

    updateContent(variant) {
      this.setText('[data-zbv4-hero-eyebrow]', variant.hero_eyebrow);
      this.setText('[data-zbv4-hero-description]', variant.hero_description);
      this.renderList('[data-zbv4-outcomes]', variant.outcomes);
      this.renderList('[data-zbv4-ingredients-verified]', variant.ingredients_verified);
      this.renderList('[data-zbv4-sensory-notes]', variant.sensory_notes);
      this.updateBrewSummary(variant.brew);
      this.updateRitualSteps(variant.ritual_steps, variant.brew?.steps);
      this.renderFaq(variant.faq);

      document.querySelectorAll('[data-zbv4-price]').forEach((element) => {
        element.textContent = this.formatMoney(variant.price);
      });
      document.querySelectorAll('[data-zbv4-availability]').forEach((element) => {
        element.textContent = variant.available ? 'In stock' : 'Sold out';
      });
    }

    updateRitualSteps(ritualSteps, brewSteps) {
      const steps = Array.isArray(ritualSteps) ? ritualSteps : [];
      const brewing = Array.isArray(brewSteps) ? brewSteps : [];
      for (let index = 0; index < 3; index += 1) {
        const stepNumber = index + 1;
        const step = steps[index] || {};
        this.setVariantText(`[data-zbv4-field="ritual-step-${stepNumber}-title"]`, step.title);
        this.setVariantText(`[data-zbv4-field="ritual-step-${stepNumber}-body"]`, step.body || brewing[index]);
      }
    }

    updateBrewSummary(brew = {}) {
      const amount = brew?.amount == null ? '' : String(brew.amount).trim();
      const water = brew?.water == null ? '' : String(brew.water).trim();
      const time = brew?.time == null ? '' : String(brew.time).trim();
      const steps = Array.isArray(brew?.steps) ? brew.steps.filter(Boolean) : [];

      this.updateBrewRow('amount', amount);
      this.updateBrewRow('water', water);
      this.updateBrewRow('time', time);
      this.renderList('[data-zbv4-brew-steps]', steps);

      const hasBrewContent = amount.length > 0 || water.length > 0 || time.length > 0 || steps.length > 0;
      document.querySelectorAll('[data-zbv4-brew-summary]').forEach((summary) => {
        summary.hidden = !hasBrewContent;
      });
    }

    updateBrewRow(kind, value) {
      const text = value == null ? '' : String(value);
      document.querySelectorAll(`[data-zbv4-brew-row="${kind}"]`).forEach((row) => {
        const field = row.querySelector(`[data-zbv4-brew-${kind}]`);
        if (field) field.textContent = text;
        row.hidden = text.length === 0;
      });
    }

    updateProductForms(variant) {
      document.querySelectorAll('[data-zbv4-product-form]').forEach((form) => {
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = String(variant.id);
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        form.querySelectorAll('[data-zbv4-add-to-cart]').forEach((button) => {
          this.updateAddButton(button, variant);
        });
      });
    }

    updateGallery(variant) {
      // The gallery is rendered from the product's own featured_media/product.media.
      // Do not replace it with custom hero/editorial image metafields.
      const featuredMediaId = String(variant.featured_media_id || '');
      const productMedia = featuredMediaId
        ? this.root.querySelector(`[data-zbv4-media="${featuredMediaId}"]`)
        : null;

      if (productMedia) {
        this.activateMedia(featuredMediaId);
      } else {
        this.activateMedia('editable-hero');
      }
    }

    updateSticky(variant) {
      const quantity = this.root.querySelector('[data-zbv4-product-form] input[name="quantity"]')?.value || '1';
      document.querySelectorAll('[data-zbv4-sticky]').forEach((sticky) => {
        const title = sticky.querySelector('[data-zbv4-sticky-title]');
        const price = sticky.querySelector('[data-zbv4-sticky-price]');
        sticky.dataset.available = String(variant.available);
        if (title) title.textContent = variant.title;
        if (price) price.textContent = this.formatMoney(variant.price);
        sticky.querySelectorAll('[data-zbv4-availability]').forEach((element) => {
          element.textContent = variant.available ? 'In stock' : 'Sold out';
        });
        sticky.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = String(variant.id);
        });
        sticky.querySelectorAll('input[name="quantity"]').forEach((input) => {
          input.value = String(quantity);
        });
        sticky.querySelectorAll('[data-zbv4-add-to-cart]').forEach((button) => {
          button.setAttribute('aria-disabled', String(!variant.available));
          this.updateAddButton(button, variant);
        });
      });
    }

    updateAddButton(button, variant) {
      button.disabled = !variant.available;
      const label = button.querySelector('[data-zbv4-add-label]');
      const text = variant.available ? (button.dataset.availableLabel || 'Add to cart') : 'Sold out';
      if (label) {
        label.textContent = text;
      } else {
        button.textContent = text;
      }
    }

    syncPurchaseMode(control) {
      if (!control.checked) return;
      const form = this.root.querySelector('[data-zbv4-product-form]');
      if (!form) return;

      let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
      if (control.value === 'subscription' && control.dataset.sellingPlanId) {
        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          form.append(sellingPlanInput);
        }
        sellingPlanInput.value = control.dataset.sellingPlanId;
      } else {
        sellingPlanInput?.remove();
      }
    }

    updateEditableMedia(kind, variant) {
      if (kind === 'hero' && this.root.dataset.zbUseSecondMedia === 'true') return;
      const imageUrl = variant[`${kind}_image_url`] || '';
      const imageSrcset = variant[`${kind}_image_srcset`] || '';
      const mobileUrl = variant[`${kind}_mobile_image_url`] || imageUrl;
      const mobileSrcset = variant[`${kind}_mobile_image_srcset`] || imageSrcset;
      const imageId = variant[`${kind}_image_id`] || '';
      const panel = this.root.querySelector(`[data-zbv4-editable-media="${kind}"]`);
      const thumbnail = this.root.querySelector(`[data-zbv4-editable-thumbnail="${kind}"]`);

      if (panel) {
        panel.dataset.zbv4EditableMediaId = String(imageId);
        if (imageUrl) {
          panel.hidden = false;
          this.updatePicture(panel, imageUrl, imageSrcset, mobileUrl, mobileSrcset, variant.title, '(min-width: 861px) 55vw, 100vw');
        } else if (kind !== 'hero') {
          panel.hidden = true;
        } else {
          panel.hidden = false;
        }
      }
      if (thumbnail) {
        thumbnail.hidden = imageUrl.length === 0 && kind !== 'hero';
        if (imageUrl) this.updatePicture(thumbnail, imageUrl, imageSrcset, mobileUrl, mobileSrcset, variant.title, '82px');
      }
    }

    updatePicture(container, imageUrl, imageSrcset, mobileUrl, mobileSrcset, alt, sizes) {
      if (!imageUrl) return;
      let picture = container.querySelector('picture');
      if (!picture) {
        picture = document.createElement('picture');
        picture.className = 'zbv4-picture';
        picture.append(document.createElement('source'), document.createElement('img'));
        container.append(picture);
      }

      const source = picture.querySelector('source');
      const image = picture.querySelector('img');
      if (source) {
        source.media = '(max-width: 749px)';
        source.srcset = mobileSrcset || mobileUrl;
        source.sizes = '100vw';
      }
      if (image) {
        image.src = imageUrl;
        image.srcset = imageSrcset;
        image.sizes = sizes;
        image.alt = String(alt || '');
      }
    }

    syncStickyQuantity(quantity) {
      document.querySelectorAll('[data-zbv4-sticky] input[name="quantity"]').forEach((input) => {
        input.value = String(Math.max(1, Number(quantity || 1)));
      });
    }

    activateMedia(mediaId) {
      const normalizedId = String(mediaId || '');
      let selectedMedia = null;
      this.root.querySelectorAll('[data-zbv4-media]').forEach((media) => {
        const isSelected = media.dataset.zbv4Media === normalizedId;
        media.hidden = !isSelected;
        if (isSelected) selectedMedia = media;
      });
      this.root.querySelectorAll('[data-zbv4-media-thumbnail]').forEach((thumbnail) => {
        thumbnail.setAttribute('aria-pressed', String(thumbnail.dataset.zbv4MediaThumbnail === normalizedId));
      });

      if (selectedMedia) {
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        selectedMedia.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    setText(selector, value) {
      document.querySelectorAll(selector).forEach((element) => {
        const text = value == null ? '' : String(value);
        element.textContent = text;
        element.hidden = text.length === 0;
      });
    }

    setVariantText(selector, value) {
      document.querySelectorAll(selector).forEach((element) => {
        if (element.dataset.zbv4ProductPrimary === 'true') return;
        if (!Object.prototype.hasOwnProperty.call(element.dataset, 'zbv4BaseText')) {
          element.dataset.zbv4BaseText = element.textContent || '';
        }
        const variantText = value == null ? '' : String(value);
        const text = variantText || element.dataset.zbv4BaseText;
        element.textContent = text;
        element.hidden = text.length === 0;
      });
    }

    renderList(selector, values) {
      const items = Array.isArray(values) ? values.filter(Boolean) : [];
      document.querySelectorAll(selector).forEach((list) => {
        const isOutcomeList = list.matches('[data-zbv4-outcomes]');
        list.replaceChildren(...items.map((value) => {
          const item = document.createElement(isOutcomeList ? 'span' : 'li');
          if (isOutcomeList) {
            item.className = 'zbv4-promise';
            const icon = document.createElement('span');
            icon.className = 'zbv4-promise__icon';
            icon.setAttribute('aria-hidden', 'true');
            icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M19.5 4.5C11.8 4.5 6.5 8.4 5.6 14.2c-.4 2.7 1.2 4.6 3.8 4.7 5.9.1 9.7-5.6 10.1-14.4Z"></path><path d="M4.5 19.5c3.1-4.1 6.5-6.8 11.4-9.2"></path></svg>';
            const copy = document.createElement('span');
            copy.className = 'zbv4-promise__text';
            copy.textContent = String(value);
            item.append(icon, copy);
          } else {
            item.textContent = String(value);
          }
          return item;
        }));
        list.hidden = items.length === 0;
      });
    }

    renderFaq(faq) {
      const entries = Array.isArray(faq) ? faq : [];
      document.querySelectorAll('[data-zbv4-faq]').forEach((container) => {
        const nodes = entries.map((entry) => {
          const details = document.createElement('details');
          const summary = document.createElement('summary');
          const answer = document.createElement('p');
          summary.textContent = String(entry.question || entry.q || '');
          answer.textContent = String(entry.answer || entry.a || '');
          details.append(summary, answer);
          return details;
        });
        container.replaceChildren(...nodes);
        container.hidden = nodes.length === 0;
      });
    }

    formatMoney(cents) {
      const currency = this.root.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-zbv4-product-root]').forEach((root) => {
      if (root.dataset.zbv4Initialized === 'true') return;
      root.dataset.zbv4Initialized = 'true';
      new ZBV4ProductController(root);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

